import React, { useState, useEffect, useCallback } from "react";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import cloneDeep from "lodash/cloneDeep";
import Form from "react-bootstrap/Form";
import Collapse from "react-bootstrap/Collapse";
import { Formik } from "formik";
import Card from "react-bootstrap/Card";
import Select from "react-select";
import { TranscriptionConstants } from "lookup/TranscriptionConstants";
import { connect } from "react-redux";

import {
  MdWarning,
  MdCancel,
  MdEdit,
  MdSave,
  MdAdd,
  MdRemove,
} from "react-icons/md";
import { FaSquare } from "react-icons/fa";
import Badge from "react-bootstrap/Badge";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import { Line } from "react-lineto";
import { startCase } from "lodash";
import "./FormSection.css";

const FormSection = (props) => {
  const { allowEditing, formSection, sectionName, updateSection, imageRef } = props;

  const drawBox = useCallback((box) => {
    let startX =
      imageRef.current.offsetLeft +
      imageRef.current.offsetParent.offsetLeft +
      imageRef.current.offsetParent.offsetParent.offsetLeft;
    let startY =
      imageRef.current.offsetTop +
      imageRef.current.offsetParent.offsetTop +
      imageRef.current.offsetParent.offsetParent.offsetTop;
    let width = 1018;
    let height = 1585;
    box = {
      topX:
        startX +
        parseInt(box.topX * (imageRef.current.offsetWidth / width)),
      topY:
        startY +
        parseInt(
          box.topY * (imageRef.current.offsetHeight / height)
        ),
      bottomX:
        startX +
        parseInt(
          box.bottomX * (imageRef.current.offsetWidth / width)
        ),
      bottomY:
        startY +
        parseInt(
          box.bottomY * (imageRef.current.offsetHeight / height)
        ),
    };
    return (
      <div>
        <Line
          x0={box.topX}
          y0={box.topY}
          x1={box.bottomX}
          y1={box.topY}
          borderColor={"red"}
          borderWidth={3}
        />
        <Line
          x0={box.topX}
          y0={box.bottomY}
          x1={box.bottomX}
          y1={box.bottomY}
          borderColor={"red"}
          borderWidth={3}
        />
        <Line
          x0={box.topX}
          y0={box.topY}
          x1={box.topX}
          y1={box.bottomY}
          borderColor={"red"}
          borderWidth={3}
        />
        <Line
          x0={box.bottomX}
          y0={box.topY}
          x1={box.bottomX}
          y1={box.bottomY}
          borderColor={"red"}
          borderWidth={3}
        />
      </div>
    );
  }, [imageRef]);

  const [isEditMode, setIsEditMode] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [formValues, setFormValues] = useState(cloneDeep(formSection));
  const [drawField, setDrawField] = useState(null);

  useEffect(() => {
    setFormValues(cloneDeep(formSection));
  }, [formSection]);

  const handleCustomChange = useCallback((fieldKey, value, setFieldValue) => {
    if (formValues[fieldKey]?.box) {
      setDrawField(fieldKey);
    }
    const newFieldData = { ...formValues[fieldKey], val: value };
    const newFormValues = { ...formValues, [fieldKey]: newFieldData };

    setFormValues(newFormValues);
    setFieldValue(fieldKey, newFieldData);
  }, [formValues]);

  const toggleShowDetails = useCallback(() => {
    setShowDetails(prev => !prev);
  }, []);

  const handleEnableEditMode = useCallback(() => {
    setIsEditMode(true);
    setShowDetails(true);
  }, []);

  const handleCancelEditMode = useCallback((resetForm) => {
    const pristineValues = cloneDeep(formSection);
    setFormValues(pristineValues);
    if (resetForm) {
      resetForm({ values: pristineValues });
    }
    setIsEditMode(false);
    setShowDetails(false);
    setDrawField(null);
  }, [formSection]);

  const handleSaveEditMode = useCallback((values, { setSubmitting }) => {
    updateSection(sectionName, formValues);
    setSubmitting(false);
    setIsEditMode(false);
    setDrawField(null);
  }, [formValues, sectionName, updateSection]);

  const renderClassificationList = useCallback((sectionName, handleBlur, setFieldValue) => {
    if (TranscriptionConstants.classificationSections.includes(sectionName)) {
      return (
        <>
          <Row>
            <Form.Group>
              <Form.Label>classifications</Form.Label>
              <Select
                options={TranscriptionConstants.classifications}
                multi={false}
                onChange={(value) =>
                  handleCustomChange(
                    "classification",
                    value,
                    setFieldValue
                  )
                }
                onBlur={handleBlur}
                value={`${sectionName}-classification`}
                disabled={!isEditMode}
                name={`${sectionName}-classification`}
              />
            </Form.Group>
          </Row>
        </>
      );
    }
    return <></>;
  }, [isEditMode, handleCustomChange]);

  const renderFormElement = useCallback((
    sectionName,
    fieldKey,
    field,
    handleBlur,
    setFieldValue,
    errors
  ) => {
    if (field.type === TranscriptionConstants.TRANSCRIBER_FIELDS.text) {
      if (!isEditMode) {
        return (
          <>
            <Row>    
              <span className="field-name pull-left">{fieldKey !== "null" ? startCase(fieldKey): ""}</span>
            </Row>
            <Row className="field-val-text">{field.val || "-"}</Row>
          </>
        );
      } else {
        return (
          <>
            <Form.Group>
              <Row>
                <Form.Label className="edit-field">
                  <span className="field-name pull-left">{fieldKey !== "null" ? startCase(fieldKey): ""}</span>
                </Form.Label>
              </Row>
              <Form.Control
                onChange={(e) => {
                  handleCustomChange(
                    e.target.name,
                    e.target.value,
                    setFieldValue
                  );
                }}
                onBlur={handleBlur}
                value={field.val}
                name={fieldKey}
                key={`${sectionName}-${fieldKey}-Control`}
                id={`${sectionName}-${fieldKey}-Control`}
                disabled={false}
                className="field-val-text"
              />
              <Row>
                <Form.Text
                  id={`${sectionName}-${fieldKey}-text`}
                  className="text-small text-danger"
                  key={`${sectionName}-${fieldKey}-text`}
                >
                  {errors.faxDisplayName &&
                    touched.faxDisplayName &&
                    errors.faxDisplayName}
                </Form.Text>
              </Row>
            </Form.Group>
          </>
        );
      }
    } else if (field.type === TranscriptionConstants.TRANSCRIBER_FIELDS.list) {
      if (field.options === null) {
        if (typeof field.val !== "boolean"){
          if (field.val === null || field.val === "false") {
            field.val = false;
          }
          else {
            field.val = true;
          }
      }
        return (
          <>
            <Row>
              <Form.Check
                inline
                onChange={(e) => {
                  handleCustomChange(
                    e.target.name,
                    e.target.checked,
                    setFieldValue
                  );
                }}
                onBlur={handleBlur}
                label={startCase(fieldKey)}
                type="checkbox"
                name={fieldKey}
                id={fieldKey}
                key={`${sectionName}-${fieldKey}`}
                fieldName={`${fieldKey}`}
                checked={field.val}
                disabled={!isEditMode}
                className="field-name pull-left"
              />
            </Row>
          </>
        );
      } else {
        if (fieldKey == "refill") {
          return (
            <>
              <Row>
                <span className="field-name pull-left">{startCase(fieldKey)}</span>
              </Row>

              <Row className="multiple-options">
                {field.options?.map((option, idx) => (
                  <Form.Check
                    inline
                    onChange={(e) => {
                      handleCustomChange(
                        e.target.name,
                        e.target.id,
                        setFieldValue
                      );
                    }}
                    onBlur={handleBlur}
                    label={option}
                    type="Radio"
                    name={fieldKey}
                    id={option}
                    key={`${sectionName}-${fieldKey}-item-${idx}`}
                    fieldName={`${fieldKey}-item-${idx}`}
                    checked={option === field.val || (!field.options.filter(item => item !== "Other").includes(field.val) && field.val !== null)}
                    disabled={!isEditMode}
                    className="field-option"
                  />
                ))}
              </Row>
              <Form.Group>
                <Form.Label>
                  <span className="other-option">other</span>
                </Form.Label>
                <Form.Control
                  onChange={(e) => {
                    handleCustomChange(
                      e.target.name,
                      e.target.value,
                      setFieldValue
                    );
                  }}
                  onBlur={handleBlur}
                  value={field.options.includes(field.val)  ? "" : field.val}
                  name={fieldKey}
                  key={`${sectionName}-${fieldKey}-Control`}
                  id={`${sectionName}-${fieldKey}-Control`}
                  disabled={field.options.filter(item => item !== "Other").includes(field.val)}
                  className="field-val-text-other"
                />
              </Form.Group>
            </>
          );
        } else {
          return (
            <>
              <Row>
                <span className="field-name pull-left">{startCase(fieldKey)}</span>
              </Row>

              <Row className="multiple-options">
                {field.options?.map((option, idx) => (
                  <Form.Check
                    inline
                    onChange={(e) => {
                      handleCustomChange(
                        e.target.name,
                        e.target.id,
                        setFieldValue
                      );
                    }}
                    onBlur={handleBlur}
                    label={option}
                    type="Radio"
                    name={fieldKey}
                    id={option}
                    key={`${sectionName}-${fieldKey}-item-${idx}`}
                    fieldName={`${fieldKey}-item-${idx}`}
                    checked={option === field.val}
                    disabled={!isEditMode}
                    className="field-option"
                  />
                ))}
              </Row>
            </>
          );
        }
      }
    }
  }, [isEditMode, handleCustomChange]);

  const setIsActiveValue = useCallback((e, handleBlur) => {
    setFormValues(prev => ({
      ...prev,
      selected: !prev.selected
    }));
      handleBlur(e);
  }, []);

    return (
      <>
        <Formik
          initialValues={{ ...formValues }}
          enableReinitialize
          onSubmit={handleSaveEditMode}
        >
          {({
            values,
            errors,
            handleBlur,
            handleSubmit,
            setFieldValue,
            resetForm,
          }) => (
            <Form onSubmit={handleSubmit} onReset={() => handleCancelEditMode(resetForm)}>
              <Row>
                <Col md={12}>
                  <Card className="form-section">
                    <Card.Body
                      className={
                        !showDetails ? "card-body-padding-override" : ""
                      }
                    >
                      <Card.Title
                        className={
                          !showDetails ? "no-margin form-card-title" : ""
                        }
                      >
                        <div className="pull-right drug-title-link-container">
                          {showDetails ? (
                            <Card.Link
                              href="#0"
                              onClick={toggleShowDetails}
                            >
                              <MdRemove className="link-icon" /> Collapse
                            </Card.Link>
                          ) : (
                            <Card.Link
                              href="#0"
                              onClick={toggleShowDetails}
                            >
                              <MdAdd className="link-icon" /> Expand
                            </Card.Link>
                          )}
                          {allowEditing ? (
                            <>
                              {isEditMode ? (
                                <>
                                  <Card.Link href="#0" onClick={() => handleCancelEditMode(resetForm)}>
                                    <MdCancel className="link-icon" /> Cancel
                                  </Card.Link>
                                  <Card.Link href="#0" onClick={handleSubmit}>
                                    <MdSave className="link-icon" /> Save
                                  </Card.Link>
                                </>
                              ) : (
                                <>
                                  <Card.Link
                                    href="#0"
                                    onClick={handleEnableEditMode}
                                  >
                                    <MdEdit className="link-icon" /> Edit
                                  </Card.Link>
                                </>
                              )}
                            </>
                          ) : (
                            <></>
                          )}
                        </div>
                        {isEditMode ? <br></br> : <></>}
                        <div className="section-name">
                          <span>
                            {sectionName.split(":")[0] === "target" ? (
                              <Form.Check
                                aria-label="Active"
                                inline
                                type="checkbox"
                                disabled={!isEditMode}
                                onBlur={handleBlur}
                                onChange={(e) =>
                                  setIsActiveValue(e, handleBlur)
                                }
                                checked={formValues.selected}
                                name="target"
                                id="target"
                              />
                            ) : (
                              <></>
                            )}
                          </span>
                          <span className="drug-title">
                            {sectionName.split(":")[0] === "target" ? (
                              <>#{sectionName.split(":")[1]} Target</>
                            ) : (
                              <>{sectionName}</>
                            )}
                          </span>
                        </div>
                      </Card.Title>
                      <Collapse in={showDetails}>
                        <div className="small-top-spacer">
                          {renderClassificationList(
                            sectionName,
                            handleBlur,
                            setFieldValue
                          )}
                          {Object.keys(formValues).map((fieldKey, ind) => {
                            if (fieldKey !== "selected") {
                              if (
                                ((fieldKey === "refill") &
                                  formValues["selected"]) |
                                (fieldKey != "refill")
                              )
                                return renderFormElement(
                                  sectionName,
                                  fieldKey,
                                  formValues[fieldKey],
                                  handleBlur,
                                  setFieldValue,
                                  errors
                                );
                            }
                          })}

                          {Object.keys(formValues).map((fieldKey, ind) => {
                            if (fieldKey === drawField) {
                              return drawBox(formValues[fieldKey].box);
                            }
                          })}
                        </div>
                      </Collapse>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </Form>
          )}
        </Formik>
      </>
    );
}

export default connect()(FormSection);
